/**
 * Data plane — Dataset Semantics (L2) editor (ADR-0026).
 *
 * Curate the MEANING of a dataset, field by field: label, description, semantic
 * type, unit, sensitivity, synonyms, role. "Proponi con AI" drafts the whole
 * descriptor via Odino (fields the AI touched are highlighted) — you review and
 * Save; nothing persists until then. Reached per-dataset from the Datasets page;
 * built reuse-ready for a future Modelli ▸ Semantica overview.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Database, Loader2, RefreshCw, Save, Sparkles } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { ProposeSemanticsDialog } from "@/components/datasets/ProposeSemanticsDialog";
import { RelationshipsSection } from "@/components/datasets/RelationshipsSection";
import { TagsField } from "@/components/tags";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { Input } from "@/components/ui/input";
import { PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { cn } from "@/lib/cn";
import { MetricsSection } from "@/components/datasets/MetricsSection";
import {
  datasetSemanticsApi,
  type DatasetSemanticsInput,
  type FieldSemantic,
  type SchemaCacheStatus,
  type Sensitivity,
} from "@/lib/api-dataset-semantics";
import { FeatureTip } from "@/components/billing/FeatureGate";
import { usePermissions } from "@/lib/permissions";

/** Badge + manual refresh for an EXTERNAL dataset's cached schema (the field list
 * is introspected over the network, then cached). Renders nothing for internal /
 * file datasets (local metadata, never cached). */
function SchemaCacheBadge({
  status,
  onRefresh,
  refreshing,
  t,
}: {
  status?: SchemaCacheStatus;
  onRefresh: () => void;
  refreshing: boolean;
  t: (key: string, fallback: string, opts?: Record<string, unknown>) => string;
}) {
  if (!status?.cacheable) return null;
  const at = status.cached_at
    ? new Date(status.cached_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : null;
  return (
    <div
      className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-surface/60 py-1 pl-2 pr-1 text-[11px] text-muted-foreground"
      title={t(
        "semantics.schemaCache.hint",
        "Lo schema di una sorgente esterna è letto via rete e tenuto in cache per velocità. «Ricarica» rilegge dalla sorgente.",
      )}
    >
      <Database className="h-3.5 w-3.5 shrink-0" strokeWidth={1.5} />
      <span>
        {status.cached && at
          ? t("semantics.schemaCache.cached", "Schema in cache · {{at}}", { at })
          : t("semantics.schemaCache.live", "Schema letto live")}
      </span>
      <Button
        variant="ghost"
        size="icon"
        className="h-5 w-5"
        onClick={onRefresh}
        disabled={refreshing}
        aria-label={t("semantics.schemaCache.refresh", "Ricarica schema")}
        title={t("semantics.schemaCache.refresh", "Ricarica schema")}
      >
        <RefreshCw className={cn("h-3.5 w-3.5", refreshing && "animate-spin")} strokeWidth={1.5} />
      </Button>
    </div>
  );
}

interface FormState {
  label: string;
  description: string;
  domain: string;
  sensitivity: Sensitivity;
  fields: Record<string, FieldSemantic>;
  /** raw db type + ordered field list, from the merged view (read-only). */
  order: { name: string; type: string | null | undefined }[];
}

function hasAnnotation(f: FieldSemantic | undefined): boolean {
  if (!f) return false;
  return Boolean(
    f.label || f.description || f.semantic_type || f.unit || f.sensitivity || f.role ||
      (f.synonyms && f.synonyms.length),
  );
}

/** Build the PUT body: dataset-level + only the fields that carry any annotation. */
function toInput(form: FormState): DatasetSemanticsInput {
  const fields: Record<string, FieldSemantic> = {};
  for (const [name, f] of Object.entries(form.fields)) {
    if (hasAnnotation(f)) fields[name] = f;
  }
  return {
    label: form.label || null,
    description: form.description || null,
    domain: form.domain || null,
    sensitivity: form.sensitivity,
    fields,
  };
}

export function DatasetSemanticsPage() {
  const { t } = useTranslation();
  const { datasetId = "" } = useParams();
  const qc = useQueryClient();
  const { can } = usePermissions();

  const vocabQ = useQuery({
    queryKey: ["datasets", "semantics-vocab"],
    queryFn: () => datasetSemanticsApi.vocab(),
    staleTime: Infinity,
  });
  const viewQ = useQuery({
    queryKey: ["datasets", datasetId, "semantics"],
    queryFn: () => datasetSemanticsApi.get(datasetId),
    enabled: Boolean(datasetId),
  });

  const [form, setForm] = React.useState<FormState | null>(null);
  const [aiTouched, setAiTouched] = React.useState<Set<string>>(() => new Set());
  const [aiNote, setAiNote] = React.useState(false);
  const [proposeOpen, setProposeOpen] = React.useState(false);

  // Seed the editing buffer once the merged view loads.
  React.useEffect(() => {
    if (!viewQ.data) return;
    const v = viewQ.data;
    const fields: Record<string, FieldSemantic> = {};
    for (const row of v.fields) {
      fields[row.name] = {
        label: row.label ?? null,
        description: row.description ?? null,
        semantic_type: row.semantic_type ?? null,
        unit: row.unit ?? null,
        sensitivity: row.sensitivity ?? null,
        synonyms: row.synonyms ?? [],
        role: row.role ?? null,
      };
    }
    setForm({
      label: v.label ?? "",
      description: v.description ?? "",
      domain: v.domain ?? "",
      sensitivity: v.sensitivity,
      fields,
      order: v.fields.map((f) => ({ name: f.name, type: f.type })),
    });
  }, [viewQ.data]);

  const save = useMutation({
    mutationFn: () => datasetSemanticsApi.save(datasetId, toInput(form!)),
    onSuccess: () => {
      setAiTouched(new Set());
      setAiNote(false);
      qc.invalidateQueries({ queryKey: ["datasets", datasetId, "semantics"] });
    },
  });

  // Manual "Ricarica schema" — re-read the live external schema (busts the field
  // cache + the source's warm engine) and adopt the fresh view (re-seeds the form).
  const refresh = useMutation({
    mutationFn: () => datasetSemanticsApi.refreshSchema(datasetId),
    onSuccess: (data) => qc.setQueryData(["datasets", datasetId, "semantics"], data),
  });

  // Merge an AI-drafted descriptor into the (still-unsaved) form, highlighting
  // every field the AI touched. Shared by the streamed debugger dialog's
  // "Applica bozza" — nothing is persisted until the operator hits Save.
  const applyDraft = React.useCallback((draft: DatasetSemanticsInput) => {
    setForm((prev) => {
      if (!prev) return prev;
      const touched = new Set<string>();
      const fields = { ...prev.fields };
      for (const [name, ann] of Object.entries(draft.fields)) {
        if (name in fields) {
          fields[name] = { ...fields[name], ...ann };
          touched.add(name);
        }
      }
      setAiTouched(touched);
      return {
        ...prev,
        label: draft.label ?? prev.label,
        description: draft.description ?? prev.description,
        domain: draft.domain ?? prev.domain,
        sensitivity: draft.sensitivity ?? prev.sensitivity,
        fields,
      };
    });
    setAiNote(true);
  }, []);

  const setField = (name: string, patch: Partial<FieldSemantic>) =>
    setForm((prev) =>
      prev ? { ...prev, fields: { ...prev.fields, [name]: { ...prev.fields[name], ...patch } } } : prev,
    );

  const sensitivityOptions: ComboboxOption[] = (vocabQ.data?.sensitivity ?? []).map((s) => ({
    value: s,
    label: t(`semantics.sensitivity.${s}`, s),
  }));
  const typeOptions: ComboboxOption[] = (vocabQ.data?.semantic_types ?? []).map((s) => ({
    value: s,
    label: s,
  }));
  const roleOptions: ComboboxOption[] = (vocabQ.data?.roles ?? []).map((s) => ({ value: s, label: s }));

  const orphans = viewQ.data?.orphans ?? [];
  const loading = viewQ.isLoading || !form;

  return (
    <PageContainer>
      <PageHeader
        title={t("semantics.title", "Semantica")}
        description={t(
          "semantics.subtitle",
          "Descrivi il significato del dataset, campo per campo. È il vocabolario su cui ragiona Odino.",
        )}
        actions={
          <div className="flex items-center gap-2">
            <SchemaCacheBadge
              status={viewQ.data?.schema_cache}
              onRefresh={() => refresh.mutate()}
              refreshing={refresh.isPending}
              t={t}
            />
            <Button variant="ghost" size="sm" asChild>
              <Link to="/datasets">
                <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
                {t("common.back", "Indietro")}
              </Link>
            </Button>
            <FeatureTip feature="ai_plane">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setProposeOpen(true)}
                disabled={loading}
              >
                <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                {t("semantics.ai.propose", "Proponi con AI")}
              </Button>
            </FeatureTip>
            <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending || loading}>
              {save.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
              ) : (
                <Save className="h-4 w-4" strokeWidth={1.5} />
              )}
              {t("common.save", "Salva")}
            </Button>
          </div>
        }
      />
      <PageContent>
        {loading ? (
          <div className="flex items-center gap-2 py-10 text-xs text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
            {t("common.loading", "Caricamento…")}
          </div>
        ) : (
          <div className="space-y-5">
            {aiNote && (
              <Alert tone="info" title={t("semantics.ai.appliedTitle", "Bozza AI applicata")}>
                {t(
                  "semantics.ai.appliedBody",
                  "I campi toccati dall'AI sono evidenziati. Rivedi e salva — niente è stato ancora salvato.",
                )}
              </Alert>
            )}

            {/* Dataset-level */}
            <section className="rounded-lg border border-border/60 p-4">
              <h3 className="mb-3 text-sm font-semibold">
                {t("semantics.section.dataset", "Livello dataset")}
              </h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <Labeled label={t("semantics.field.label", "Etichetta")}>
                  <Input
                    value={form.label}
                    onChange={(e) => setForm({ ...form, label: e.target.value })}
                    placeholder={t("semantics.field.labelPh", "es. Contatti CRM")}
                  />
                </Labeled>
                <Labeled label={t("semantics.field.domain", "Dominio")}>
                  <Input
                    value={form.domain}
                    onChange={(e) => setForm({ ...form, domain: e.target.value })}
                    placeholder={t("semantics.field.domainPh", "es. CRM, Contabilità")}
                  />
                </Labeled>
                <Labeled label={t("semantics.field.sensitivity", "Sensibilità")}>
                  <Combobox
                    options={sensitivityOptions}
                    value={form.sensitivity}
                    onChange={(v) => v && setForm({ ...form, sensitivity: v as Sensitivity })}
                  />
                </Labeled>
                <Labeled label={t("semantics.field.description", "Descrizione")} className="sm:col-span-2">
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={2}
                    className="flex w-full rounded-md border border-border bg-surface px-2.5 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    placeholder={t("semantics.field.descriptionPh", "A cosa serve questo dataset?")}
                  />
                </Labeled>
              </div>
            </section>

            {/* Fields */}
            <section className="overflow-hidden rounded-lg border border-border/60">
              <div className="border-b border-border/60 bg-surface px-3 py-2 text-sm font-semibold">
                {t("semantics.section.fields", "Campi")}{" "}
                <span className="text-xs font-normal text-muted-foreground">({form.order.length})</span>
              </div>
              <Table>
                <Thead>
                  <Tr>
                    <Th>{t("semantics.col.field", "Campo")}</Th>
                    <Th>{t("semantics.col.type", "Tipo semantico")}</Th>
                    <Th>{t("semantics.col.label", "Etichetta")}</Th>
                    <Th className="w-24">{t("semantics.col.unit", "Unità")}</Th>
                    <Th className="w-36">{t("semantics.col.sensitivity", "Sensibilità")}</Th>
                    <Th className="w-32">{t("semantics.col.role", "Ruolo")}</Th>
                    <Th>{t("semantics.col.synonyms", "Sinonimi")}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {form.order.map(({ name, type }) => {
                    const f = form.fields[name] ?? {};
                    const touched = aiTouched.has(name);
                    return (
                      <Tr key={name} className={cn(touched && "bg-info/5")}>
                        <Td className="align-top">
                          <div className="font-medium">{name}</div>
                          {type && <div className="font-mono text-[10px] text-muted-foreground">{type}</div>}
                        </Td>
                        <Td className="align-top">
                          <Combobox
                            options={typeOptions}
                            value={f.semantic_type ?? null}
                            onChange={(v) => setField(name, { semantic_type: v })}
                            allowCustom
                            placeholder="—"
                          />
                        </Td>
                        <Td className="align-top">
                          <Input
                            value={f.label ?? ""}
                            onChange={(e) => setField(name, { label: e.target.value })}
                          />
                        </Td>
                        <Td className="align-top">
                          <Input
                            value={f.unit ?? ""}
                            onChange={(e) => setField(name, { unit: e.target.value })}
                            placeholder="EUR…"
                          />
                        </Td>
                        <Td className="align-top">
                          <Combobox
                            options={sensitivityOptions}
                            value={f.sensitivity ?? null}
                            onChange={(v) => setField(name, { sensitivity: (v as Sensitivity) ?? null })}
                            placeholder="—"
                          />
                        </Td>
                        <Td className="align-top">
                          <Combobox
                            options={roleOptions}
                            value={f.role ?? null}
                            onChange={(v) => setField(name, { role: v })}
                            allowCustom
                            placeholder="—"
                          />
                        </Td>
                        <Td className="align-top">
                          <TagsField
                            value={f.synonyms ?? []}
                            onChange={(next) => setField(name, { synonyms: next })}
                          />
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </section>

            {/* Relationships (knowledge graph) */}
            <RelationshipsSection datasetId={datasetId} />

            {/* Declared metrics (ADR-0029) — the agent's "CLAUDE.md" */}
            <MetricsSection
              datasetId={datasetId}
              fields={form?.order ?? []}
              aggregations={vocabQ.data?.aggregations ?? []}
              canWrite={can("semantics", "write")}
            />

            {/* Orphans */}
            {orphans.length > 0 && (
              <section className="rounded-lg border border-warning/40 bg-warning/5 p-4">
                <h3 className="mb-1 text-sm font-semibold">
                  {t("semantics.orphans.title", "Annotazioni orfane")}
                </h3>
                <p className="mb-2 text-xs text-muted-foreground">
                  {t(
                    "semantics.orphans.body",
                    "Questi campi non esistono più nel dataset. Le loro annotazioni restano finché non salvi senza di esse.",
                  )}
                </p>
                <div className="flex flex-wrap gap-2">
                  {orphans.map((o) => (
                    <Badge key={o.name} tone="warning">
                      {o.name}
                      {o.semantic_type ? ` · ${o.semantic_type}` : ""}
                    </Badge>
                  ))}
                </div>
              </section>
            )}

            {save.isError && (
              <Alert tone="danger" title={t("common.saveFailed", "Salvataggio fallito")}>
                {(save.error as Error).message}
              </Alert>
            )}
          </div>
        )}
      </PageContent>
      <ProposeSemanticsDialog
        open={proposeOpen}
        onOpenChange={setProposeOpen}
        datasetId={datasetId}
        onApply={applyDraft}
      />
    </PageContainer>
  );
}

function Labeled({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={cn("block space-y-1", className)}>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
